'use client'

import { setCookie } from 'cookies-next/client'
import { type FC, useContext, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import { Notification } from 'base-ui'

import { COOKIE_GUEST_EMAIL_NAME } from '../../../cookies'
import { useHookFormFriendlyCaptchaFieldControls } from '../../../friendly-captcha'
import { useGlobalConfigContext } from '../../../global-config'
import { urlResolverGetLogin } from '../../../url-handling'
import { LoginRegistrationUserLoginStateContext } from '../../context/LoginRegistrationUserLoginStateContext'
import { type LoginAfterLoggedIn } from '../../loginAfterLoggedIn'

import { LoginRegistrationOneTimePasswordCodePanel } from './LoginRegistrationOneTimePasswordCodePanel'
import { LoginRegistrationOneTimePasswordFormActions } from './LoginRegistrationOneTimePasswordFormActions'
import { type LoginRegistrationOneTimePasswordInputFormData } from './loginRegistrationOneTimePasswordInputFormData'
import { makeLoginRegistrationOneTimePasswordNavigationHandlers } from './makeLoginRegistrationOneTimePasswordNavigationHandlers'
import { useLoginRegistrationOneTimePasswordSubmission } from './useLoginRegistrationOneTimePasswordSubmission'
import { useShowOneTimePasswordReRequestMessage } from './useShowOneTimePasswordReRequestMessage'

interface LoginRegistrationOneTimePasswordInputFormProps {
  afterLoggedIn?: LoginAfterLoggedIn
  onAcceptedSubmit?: () => void
  onBackToPassword?: () => void
  onChangeEmail?: () => void
  onSubmissionFailure?: () => void
}

export const LoginRegistrationOneTimePasswordInputForm: FC<
  LoginRegistrationOneTimePasswordInputFormProps
> = ({
  afterLoggedIn,
  onAcceptedSubmit,
  onBackToPassword,
  onChangeEmail,
  onSubmissionFailure,
}) => {
  const globalConfig = useGlobalConfigContext()

  const { otpQuery, setLoginOtpState, setUsername, username } = useContext(
    LoginRegistrationUserLoginStateContext,
  )
  const formMethods = useForm<LoginRegistrationOneTimePasswordInputFormData>({
    defaultValues: {
      fcSolution: '',
      password: otpQuery,
    },
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })
  const { handleSubmit, register, setValue, watch } = formMethods
  const { onCaptchaDone, watchFriendlyCaptchaChange } =
    useHookFormFriendlyCaptchaFieldControls({
      fieldName: 'fcSolution',
      register,
      setValue,
      watch,
    })

  const showReRequestMessage = useShowOneTimePasswordReRequestMessage()

  useEffect(() => {
    if (!username) {
      window.location.assign(urlResolverGetLogin(globalConfig))
    }
  }, [username, globalConfig])

  const submission = useLoginRegistrationOneTimePasswordSubmission({
    afterLoggedIn,
    handleSubmit,
    onAcceptedSubmit,
    onSubmissionFailure,
    username,
  })

  const { handleBackToPassword, handleChangeEmail, handleRequestNewCode } =
    makeLoginRegistrationOneTimePasswordNavigationHandlers({
      clearGuestEmail: () => {
        setCookie(COOKIE_GUEST_EMAIL_NAME, '')
      },
      onBackToPassword,
      onChangeEmail,
      setLoginOtpState,
      setUsername,
    })

  return (
    <FormProvider {...formMethods}>
      <h2 className="mb-2 text-l font-medium">
        <FormattedMessage id="login.otp.withCode" />
      </h2>
      {submission.showGeneralErrorNotification ? (
        <Notification severity="error">
          <FormattedMessage id="general.error" />
        </Notification>
      ) : null}
      <p className="mb-4 text-m font-normal">
        <FormattedMessage
          id="login.otp.emailSent"
          values={{
            email: username,
          }}
        />
      </p>
      <form
        action={urlResolverGetLogin(globalConfig)}
        data-clientside-hook="formValidation"
        method="POST"
        name="otp-login-form"
        onSubmit={submission.handleFormSubmit}
      >
        <LoginRegistrationOneTimePasswordCodePanel
          apiErrorMessage={submission.apiErrorMessage}
          onRequestNewCode={handleRequestNewCode}
          showReRequestMessage={showReRequestMessage}
        />
        <LoginRegistrationOneTimePasswordFormActions
          captchaDoneCallback={onCaptchaDone}
          isFriendlyCaptchaDone={Boolean(watchFriendlyCaptchaChange)}
          isMutating={submission.isMutating}
          mutationError={submission.mutationError}
          onBackToPassword={handleBackToPassword}
          onChangeEmail={handleChangeEmail}
        />
      </form>
    </FormProvider>
  )
}
